import { differenceInCalendarDays, ZonedDateTimeFactory } from '@csp/csp-common-date-util';
import {
  DataChangeDomain,
  DataChangeEvidenceMapper,
  DataChangeItem,
  DataChangeReason,
  DataChangeSource,
  DataChangeStatus,
  DataChangeTag,
  DataChangeType,
} from '@csp/csp-common-dcf-model';
import { EnumUtil } from '@csp/csp-common-enum-util';
import { Patient, StateAssert, Study } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { DataChangeInV1, DataChangeV1 } from '@csp/dmdp-api-data-change-dto';
import { DataChangeIn } from '../model/DataChangeIn';
import { generateDcRef } from '../util/GenerateDcRef';

const toDataChangeItem = (dc: DataChangeV1, study: Study): DataChangeItem => {
  const {
    domain,
    dataSourceGroup,
    dcRef,
    reasonForChange,
    typeOfChange,
    reviewStatus,
    createdAt: createdAtProp,
    reviewedAt,
    dataSource,
    dataSourceId,
    reviewedBy,
    createdBy,
    dcId,
    eCode,
    studySiteId,
    userId,
    evidenceForChangeExists,
    tags,
    orgId,
  } = dc;

  const createdAt = ZonedDateTimeFactory.fromZonedDateTimeString(createdAtProp);
  // Pick orgId if it exists, but fallback to studySiteId for data changes created before R12
  const siteId = orgId ?? study.getSiteByCtmsId(studySiteId)?.orgId;

  const hasEvidence = DataChangeEvidenceMapper.toEnum(evidenceForChangeExists);

  StateAssert.isDefined(siteId, `No site ID found on datachange ${dcId}`);

  return {
    dataName: dataSourceGroup,
    domain: EnumUtil.fromStringOrDefault(domain, DataChangeDomain, DataChangeDomain.UNKNOWN),
    reasonForChange: EnumUtil.fromStringOrError(reasonForChange, DataChangeReason),
    typeOfChange: EnumUtil.fromStringOrDefault(typeOfChange, DataChangeType, DataChangeType.UNKNOWN),
    status: EnumUtil.fromStringOrDefault(reviewStatus, DataChangeStatus, DataChangeStatus.UNKNOWN),
    isPending: reviewStatus === DataChangeStatus.PENDING_REVIEW,
    dataChangeReference: dcRef,
    requiresReview: reviewStatus !== DataChangeStatus.IMPLEMENTED,
    isReviewed: reviewStatus === DataChangeStatus.REVIEWED,
    reviewedAt: reviewedAt ? ZonedDateTimeFactory.fromZonedDateTimeString(reviewedAt) : undefined,
    createdAt,
    daysSinceCreated: differenceInCalendarDays(ZonedDateTimeFactory.now(), createdAt),
    dataSource: EnumUtil.fromStringOrDefault(dataSource, DataChangeSource, DataChangeSource.UNKNOWN),
    dataSourceId,
    reviewedBy,
    createdBy,
    id: dcId,
    ecode: eCode,
    siteId,
    patientId: userId,
    hasEvidence,
    tags: tags.map(tag => EnumUtil.fromStringOrError(tag, DataChangeTag)),
  };
};

const toDataChangeItems = (dcs: DataChangeV1[], study: Study): DataChangeItem[] =>
  dcs.map(dc => toDataChangeItem(dc, study));

const toDataChangeInV1 = (patient: Patient, dataChangeIn: DataChangeIn): DataChangeInV1 => {
  const {
    dataSourceIn,
    evidenceForChangeExists,
    reasonForChange,
    reviewStatus,
    tags = [],
    typeOfChange,
  } = dataChangeIn;
  const { firstOrgId, ecode, userId } = patient;

  StateAssert.isDefined(firstOrgId, `No site ID found on patient ${userId}`);

  return {
    dataSource: dataSourceIn.dataSource,
    dataSourceGroup: dataSourceIn.dataSourceGroup,
    dataSourceId: dataSourceIn.dataSourceId,
    dcRef: generateDcRef(ecode),
    domain: dataSourceIn.domain,
    eCode: ecode,
    ...(isDefined(evidenceForChangeExists) ? { evidenceForChangeExists } : {}),
    orgId: firstOrgId,
    reasonForChange,
    reviewStatus,
    tags,
    typeOfChange,
    userId,
  };
};

export const DataChangeMapper = {
  toDataChangeItem,
  toDataChangeItems,
  toDataChangeInV1,
};
